import * as Yup from "yup";
import { Link, Redirect, useLocation } from "react-router-dom";
import AuthLayout, { FormHeader } from "components/auth/AuthLayout";
import Button from "components/global/Button";
import FormGroup from "components/global/FormGroup";
import { Form, Formik } from "formik";
import Spacer from "components/global/Spacer";
import { connect } from "react-redux";
import { axiosInstance, loginUser } from "api";
import { showAlert } from "utils/utils";
import Logo from "components/global/Logo";
import { useEffect, useState } from "react";

const LoginWithCode = ({
  logged_in,
  user_accounts,
  user
}) => {
  const { search } = useLocation();
  const redirectPath = new URLSearchParams(search).get("return_to");
  const email = new URLSearchParams(search).get("login_email");
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(60);

  const schema = Yup.object({
    code: Yup.string().required("Field required"),
  });

  const handleLogin = async (values, setSubmitting) => {
    const userData = {
      email: email,
      otp: values.code,
    };
    const res = await loginUser(userData);
    setSubmitting(false);
    if (res?.data?.status === false || res?.data?.errors) {
      showAlert({
        msg: res?.data?.message ?? "Incorrect email or password",
        type: "danger",
      });

      return;
    }
  };

  const requestLoginCode = async () => {
    setIsSendingCode(true);
    try {
      const res = await axiosInstance.post(`get-login-code`, {
        email: email,
      });
      if (res.data.status === true) {
        showAlert({
          msg: "Code sent",
          type: "success",
        });
      }
      setIsSendingCode(false);
    } catch (error) {
      setIsSendingCode(false);
      showAlert({
        msg: error.response?.data?.message ?? "Failed to send code",
        type: "danger",
      });
    }
  };

  useEffect(() => {
    if (countdown <= 0) return;

    const timerId = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [countdown]);

  if (!email) {
    return <Redirect to={"/dashboard/home"} />;
  }

  if (user?.email_verified_at && redirectPath?.includes("accept-invite")) {
    return <Redirect to={redirectPath ?? "/dashboard/home"} />;
  }

  if (logged_in && user_accounts !== null)
    return (
      <Redirect
        to={
          !user?.has_viewed_tooltips?.onboarding_completed
            ? "/onboarding/create-brand"
            : !user_accounts?.length && !redirectPath?.includes("accept-invite")
            ? "/onboarding/create-brand"
            : user_accounts?.length && !redirectPath?.includes("accept-invite")
            ? redirectPath ?? "/dashboard/home"
            : "/dashboard/home"
        }
      />
    );

  return (
    <AuthLayout>
      <FormHeader>
        <Logo />
        <p className="l3">
          Don't have an account?{" "}
          <Link to="/sign-up" className="signInText l3">
            Sign up
          </Link>
        </p>
      </FormHeader>
      <Spacer y={10} yMobile={8} />
      <h3 className="title colorGrey1">Login with code</h3>
      <Spacer y={0.6} />
      <p className="description">We sent a temporary login code to {email}.</p>
      <Spacer y={2.4} />
      <Formik
        initialValues={{
          code: "",
        }}
        validationSchema={schema}
        onSubmit={(values, { setSubmitting }) => {
          handleLogin(values, setSubmitting);
        }}
      >
        {({ isSubmitting, isValid, values }) => (
          <Form>
            <FormGroup
              fieldStyle="shortText"
              type="text"
              label="Login code"
              name="code"
              className="spanFull"
            />

            <span className="resend_code_container">
              Didn’t get the code?{" "}
              {countdown === 0 ? (
                <button
                  type="button"
                  onClick={requestLoginCode}
                  disabled={isSendingCode}
                >
                  Resend code
                </button>
              ) : (
                `Resend code in ${countdown} seconds`
              )}
            </span>

            <Button
              type="submit"
              text="Login"
              className="spanFull"
              disabled={isSubmitting || !isValid || !values.code}
              loading={isSubmitting}
              fullWidth
            />
          </Form>
        )}
      </Formik>

      <Link
        to="/sign-in"
        style={{ margin: "24px auto", display: "block", textAlign: "center" }}
      >
        Not you?
      </Link>
    </AuthLayout>
  );
};

const mapStateToProps = (state) => {
  return {
    logged_in: state.logged_in,
    user_accounts: state.user_accounts,
    user: state.user
  };
};

export default connect(mapStateToProps)(LoginWithCode);
